/* eslint-disable no-underscore-dangle, camelcase */
import groupBy from 'lodash/groupBy';
import merge from 'lodash/merge';
import _sortBy from 'lodash/sortBy';
import _uniqBy from 'lodash/uniqBy';
import queryString from 'query-string';
import {
  JourneyApplicationEventTypesMap,
  JourneyNodeTypesMap,
  JourneyTypesMap,
} from '@usealloy/typegen';

import { convertToCamel } from '~/helpers/api';
import { normalizeString, snakeToCaps } from '~/helpers/formatting';
import { ACTION_NODE_TYPES } from '~/journey/constants';
import { MANUAL_REVIEW_OUTCOME } from '~/journey/outcomes';

import {
  business,
  DEFAULT_MANUAL_REVIEW_NODE_ID,
  DEFAULT_MANUAL_REVIEW_OUTCOMES_NAMES,
  DEFAULT_POST_EOJ_RECON_MANUAL_REVIEW_NODE_ID,
  ALERTS_MANUAL_REVIEW_OUTCOMES_NAMES,
  EXCLUDE_EVENT_TYPES_FROM_JOURNEY_STEPS,
  group,
  groupTypes,
  NODE_TYPES_TO_MAP_TO_JOURNEY_STEP,
  POST_COMPLETED_JA_MANUAL_REVIEW_NODE_ID,
  QUERY_PARAM_NAME_TO_STATE_VAR,
  STATE_VAR_TO_QUERY_PARAM_NAME,
} from './constants';

const getReviewName = event => {
  const { node } = event._embedded ?? {};
  if (node.name) return node.name;
  return event.journey_type === JourneyTypesMap.ALERT ? 'Alert Review' : 'Application Review';
};

const getJourneyStepName = ({
  actionName,
  actionVersion,
  nodeType,
  workflowName,
  workflowVersion,
}) => {
  switch (nodeType) {
    case JourneyNodeTypesMap.ACTION:
      return `${actionName} v.${actionVersion}`;
    case JourneyNodeTypesMap.REVIEW:
      return 'Application Review';
    case JourneyNodeTypesMap.DOCV:
    case JourneyNodeTypesMap.WORKFLOW:
    case JourneyNodeTypesMap.RECONCILIATION_WORKFLOW:
      return `${workflowName} v.${workflowVersion}`;
    case JourneyNodeTypesMap.RETRY:
      return 'Retry';
    default:
      return null;
  }
};

const getJourneyStepStatus = ({ eventType, outcome }) => {
  if (eventType === 'error') return 'Error';
  if (eventType.startsWith('completed')) return outcome;
  if (eventType === 'pending_action_expired') return 'Expired';
  if (eventType === 'pending_step_up_expired') return 'Expired';
  if (eventType === 'reached_auto_decision_node') return outcome;
  return 'Pending';
};

const checkIfJourneyApplicationReviewIsAfterReconWorkflow = ({
  acc,
}) => {
  const [previousEvent] = acc.slice(-1);
  return previousEvent && previousEvent.nodeType === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW;
};

export const formatJourneySteps = ({
  entities,
  events,
}) => events.reduce((acc, event, i, thisArr) => {
  /**
   * We still need to process JA level review events here. They may be attached
   * to reconciliation evaluation completed events.
   *
   * They will still be filtered out of the overall journey steps further down.
   * @type {boolean}
   */
  const {
    COMPLETED_EVALUATION,
    COMPLETED_MID_JOURNEY_RECONCILIATION,
    COMPLETED_REVIEW_JOURNEY_APPLICATION,
    REACHED_MID_JOURNEY_RECONCILIATION_NODE,
    STARTED_MID_JOURNEY_RECONCILIATION,
  } = JourneyApplicationEventTypesMap;

  const { type: eventType } = event;

  // For Mid-Journey Recon events "reached..." & "started..." we want to check if
  // the next event in the series of "reached..." & "started..." & "completed..."
  // occurs and if so we pass over the current event as we would rather display
  // the later one to the user
  //
  // NOTE: This is potentially unecessary if we can find a way to just sort the
  // events passed to `formatJourneySteps` by their timestamp and do away with
  // `sortEventsByEntity`
  if (
    eventType === COMPLETED_MID_JOURNEY_RECONCILIATION
      || eventType === REACHED_MID_JOURNEY_RECONCILIATION_NODE
      || eventType === STARTED_MID_JOURNEY_RECONCILIATION
  ) {
    const eventsGroupedByType = groupBy(thisArr, 'type');
    const completedMJReconEvents = eventsGroupedByType[COMPLETED_MID_JOURNEY_RECONCILIATION];
    const completedEventForSameNode = completedMJReconEvents?.find(
      mjReconEvent => mjReconEvent._embedded.node.id === event._embedded.node.id,
    );

    if (completedEventForSameNode) {
      return acc;
    }

    const startedMJReconEvents = eventsGroupedByType[STARTED_MID_JOURNEY_RECONCILIATION];
    const startedEventForSameNode = startedMJReconEvents?.find(
      mjReconEvent => mjReconEvent._embedded.node.id === event._embedded.node.id,
    );

    if (startedEventForSameNode) {
      return acc;
    }

    // We need this check because the order of events that we get back from
    // `sortEventsByEntity` places the `completed_evaluation` event after the
    // `completed_mid_journey_reconciliation_event` so the `precedesSameNode` and
    // `precedesSameEntity` check below won't pass but we only want to show one
    // of these events
    const completedEvaluationEvents = eventsGroupedByType[COMPLETED_EVALUATION];
    const completedEvaluationEventForSameNode = completedEvaluationEvents?.find(
      evaluationEvent => (
        evaluationEvent._embedded.node.id === event._embedded.node.id
          && evaluationEvent.entity_token === event.entity_token
          && evaluationEvent.evaluation_token === event.evaluation_token
      ),
    );

    if (completedEvaluationEventForSameNode) {
      return acc;
    }
  }

  const isJourneyApplicationReview = eventType === COMPLETED_REVIEW_JOURNEY_APPLICATION;
  const isJAReviewAfterReconWorkflow = checkIfJourneyApplicationReviewIsAfterReconWorkflow({
    acc,
  });

  if (isJourneyApplicationReview && !isJAReviewAfterReconWorkflow) {
    return acc;
  }

  // To account for multi entity applications need to include a check for if the event is for the same entity.
  const { node } = event._embedded ?? {};
  const nodeConfig = node?.config;
  const nodeType = isJourneyApplicationReview ? JourneyNodeTypesMap.REVIEW : node?.type;
  const precedesSameNode = event._embedded.node.id === thisArr[i + 1]?._embedded.node.id;
  const precedesSameEntity = event.entity_token === thisArr[i + 1]?.entity_token;

  if (precedesSameNode && precedesSameEntity && !isJourneyApplicationReview) {
    return acc;
  }

  if (
    !NODE_TYPES_TO_MAP_TO_JOURNEY_STEP.has(nodeType)
      || EXCLUDE_EVENT_TYPES_FROM_JOURNEY_STEPS.has(eventType)
  ) {
    return acc;
  }

  let journeyStepStatus = getJourneyStepStatus({
    eventType,
    outcome: event.outcome,
  });

  /**
   * `started_application` events for Alerts with source WFs (from regular or portfolio evaluations)
   * don't include the `outcome` of the source eval on the event. We can find it, to show it correctly
   * in the stepper, on the `entity_application_branch_name` field for the entity. This does assume
   * single entity Alerts and will need to be reworked for future ongoing MEJ.
   */
  if (
    eventType === JourneyApplicationEventTypesMap.STARTED_APPLICATION
    && nodeType === JourneyNodeTypesMap.SOURCE_WORKFLOW
    && journeyStepStatus === 'Pending'
    && (
      event.evaluation_token
      || event.portfolio_evaluation_token
    )
  ) {
    const sourceWfOutcome = entities?.[0]?.entity_application_branch_name;
    journeyStepStatus = sourceWfOutcome ?? journeyStepStatus;
  }

  const formattedJourneyStep = {
    nodeType,
    caseToken: event.case_token,
    alertManualReviewToken: event.alert_manual_review_token,
    entityApplicationToken: event.entity_application_token,
    entityToken: event.entity_token,
    evaluationToken: event.evaluation_token,
    portfolioEvaluationToken: event.portfolio_evaluation_token,
    portfolioEvaluationRunTimestamp: event.portfolio_evaluation_run_timestamp,
    evaluationTokensSortedByCreatedDesc: event.evaluation_tokens_sorted_by_created_dec,
    journeyApplicationEventToken: event.journey_application_event_token,
    name: getJourneyStepName({
      nodeType,
      actionName: nodeConfig?.action_name,
      actionVersion: nodeConfig?.action_version,
      workflowName: node.workflow_name,
      workflowVersion: nodeConfig?.workflow_version_id,
    }),
    nodeId: node.id,
    visitedNodeAt: event.timestamp,
    ...(nodeConfig?.action_name && {
      action: {
        journeyActionId: nodeConfig?.journey_action_id,
        journeyActionVersionId: nodeConfig?.journey_action_version_id,
        name: nodeConfig?.action_name,
        type: nodeConfig?.action_type,
        version: nodeConfig?.action_version,
      },
    }),
    status: journeyStepStatus,
    ...massageEventData(event, thisArr, i),
  };

  if (
    nodeType === 'review'
    || nodeConfig?.action_type === ACTION_NODE_TYPES.CUSTOM_REVIEW
    || nodeConfig?.action_type === ACTION_NODE_TYPES.PARENT_REVIEW
  ) {
    const [previousEvent] = acc.slice(-1);

    if (previousEvent) {
      if (!previousEvent.reviews) previousEvent.reviews = [];
      formattedJourneyStep.name = getReviewName(event);
      formattedJourneyStep.reason = event.reason;
      formattedJourneyStep.reasons = event.reasons || (event.reason ? [event.reason] : []);
      previousEvent.reviews.push(formattedJourneyStep);
      previousEvent.review = convertToCamel(event);
    }

    return acc;
  }


  /**
   * If there are multiple JA level reviews, we'll end up attaching the latest one
   * to the reconciliation evaluation completed event.
   *
   * This also serves to filter the JA level review events themselves out of the
   * journey steps.
   */
  if (eventType === COMPLETED_REVIEW_JOURNEY_APPLICATION) {
    const reconEvalCompletedEvent = acc.find(
      step => step.nodeType === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW);

    if (reconEvalCompletedEvent) {
      reconEvalCompletedEvent.review = formattedJourneyStep;
      reconEvalCompletedEvent.status = formattedJourneyStep.status;
    }

    return acc;
  }

  acc.push(formattedJourneyStep);

  return acc;
}, []);

const formatEntityData = entityData => {
  const {
    external_entity_identifier: externalEntityIdentifier,
    name,
    token,
    type,
    entity_application_token: entityApplicationToken,
    entity_application_status: entityApplicationStatus,
  } = entityData || {};
  return {
    externalEntityIdentifier,
    name,
    token,
    type,
    entityApplicationStatus,
    entityApplicationToken,
    sandboxPersonaName: null,
  };
};

const formatEntityApplications = entityApplications => entityApplications.map(ea => ({
  entityToken: ea.entity_token,
  dimensions: ea.dimensions,
  sdkLink: ea.sdk_link,
}));

function generateWorkflowObjectForNode(node) {
  const { config = {} } = node;

  return {
    name: node.workflow_name,
    token: config.workflow_token,
    type: config.workflow_type,
    version: config.workflow_current_version,
  };
}

/** Here we want to alter some of the key-value pairs in formatJourneySteps
  * and then spread the returned object over the formattedJourneyStep - so
  * the result is that we are "massaging" the data in the formattedJourneyStep
  * @param {Object} event - The current Application Event we want to transform
  * data from and return
  * @param {Array} events - The Application Events, each Node in a Journey and
  * it's action that it performs creates an Event and we want to grab some of
  * that data here
  * @param {Number} index - Current array index of the events argument passed
  * @returns {Object}
  */
function massageEventData(event, events, index) {
  switch (event.type) {
    case JourneyApplicationEventTypesMap.MAXIMUM_RETRIES_REACHED: {
      // In the case where we reach the max number of retries we want to grab all
      // of the events up to just before the retry event that comes before the
      // max retries event (e.g. excluding said retry event) and then walk
      // backwards (via .reverse().find()) through that array looking for an event
      // adjacent to a retry event - which should be our retried event
      const retriedEvent = events.slice(0, index - 1).reverse().find(
        (_event, i, allEvents) => (
          allEvents[i + 1]?.type === JourneyApplicationEventTypesMap.REACHED_RETRY_NODE
        ),
      );

      return {
        ...massageRetryEvent(
          event,
          retriedEvent,
          { workflow: { name: 'Retries Exceeded', version: '' } },
        ),
        status: 'retries_exceeded',
      };
    }
    case JourneyApplicationEventTypesMap.REACHED_RETRY_NODE: {
      // The retry action will trigger the next event. This assumes that events are ordered chronologically
      const retriedEvent = events.find(e => (
        e.timestamp > event.timestamp
        && e.entity_application_token === event.entity_application_token
      ));

      return massageRetryEvent(
        event,
        retriedEvent,
        { workflow: { name: 'Retry', version: '' } },
      );
    }
    default: {
      if (event._embedded.node?.config?.workflow_token) {
        return { workflow: generateWorkflowObjectForNode(event._embedded.node) };
      }

      return {};
    }
  }
}

/** Here we want to alter some of the key-value pairs in retriedEvent
 * and then spread the returned object over the formattedJourneyStep in
 * formatJourneySteps - so the result is that we are "massaging"
 * (or "partially transforming") the data in the formattedJourneyStep
 * @param {Object} event - The current Application Event we want to use to
 * grab data from and transform formattedJourneyStep
 * @param {Object} retriedEvent - The Application Event that the Retry Node is,
 * retrying
 * @returns {Object}
 */
function massageRetryEvent(event, retriedEvent, overrides = {}) {
  if (!retriedEvent) { return {}; }

  const node = retriedEvent._embedded?.node ?? {};
  let workflow;

  switch (node.type) {
    case JourneyNodeTypesMap.REVIEW: {
      workflow = { name: 'Manual Review', type: JourneyNodeTypesMap.REVIEW };

      break;
    }
    case JourneyNodeTypesMap.ACTION: {
      const { config = {} } = node;
      workflow = {
        name: config.action_name,
        type: JourneyNodeTypesMap.ACTION,
        version: config.action_version,
      };

      break;
    }
    default:
      workflow = {
        ...generateWorkflowObjectForNode(node),
        // We are overriding the type that would be node.config.workflow_type
        // with just a type: 'workflow' in the case where there is a
        // workflow_type (which should only exist on Workflows) so that in the
        // RetryJourneyStepView we can switch on this and display the right header
        ...(
          node.config?.workflow_type
            ? { type: JourneyNodeTypesMap.WORKFLOW }
            : {}
        ),
      };
  }

  return (
    retriedEvent
      ? merge({
        workflow,
        evaluationTokensSortedByCreatedDesc: retriedEvent.evaluation_tokens_sorted_by_created_dec,
        // We are essentially flipping the workflow name with the event name
        // so that the JourneyStep in the EntityApplicationCard will show 'Retry'
        // instead of the workflow name but, we want to show this workflow name
        // under the "Workflow" header in the step details
        name: `${workflow.name}${workflow.version ? ` v.${workflow.version}` : ''}`,
        retryLimit: event._embedded.node.config.retry_limit,
        status: JourneyNodeTypesMap.RETRY,
      }, overrides)
      : {}
  );
}

export const sortEventsByEntity = events => _sortBy(events, 'timestamp').sort((a, b) => {
  const {
    COMPLETED_MID_JOURNEY_RECONCILIATION,
    COMPLETED_REVIEW_JOURNEY_APPLICATION,
    REACHED_MID_JOURNEY_RECONCILIATION_NODE,
    STARTED_APPLICATION,
    STARTED_MID_JOURNEY_RECONCILIATION,
  } = JourneyApplicationEventTypesMap;

  const { RECONCILIATION_WORKFLOW, SOURCE_WORKFLOW } = JourneyNodeTypesMap;
  const {
    entity_token: aToken,
    type: aEventType,
    _embedded: { node: { type: aNodeType } },
  } = a;

  const {
    entity_token: bToken,
    type: bEventType,
    _embedded: { node: { type: bNodeType } },
  } = b;

  const eventAIsJA = (
    aToken === null
      || (
        aNodeType === RECONCILIATION_WORKFLOW
          && aEventType !== COMPLETED_MID_JOURNEY_RECONCILIATION
          && aEventType !== REACHED_MID_JOURNEY_RECONCILIATION_NODE
          && aEventType !== STARTED_MID_JOURNEY_RECONCILIATION
      )
      || aEventType === COMPLETED_REVIEW_JOURNEY_APPLICATION
  );

  const eventBIsJA = (
    bToken === null
      || (
        bNodeType === RECONCILIATION_WORKFLOW
          && bEventType !== COMPLETED_MID_JOURNEY_RECONCILIATION
          && bEventType !== REACHED_MID_JOURNEY_RECONCILIATION_NODE
          && bEventType !== STARTED_MID_JOURNEY_RECONCILIATION
      )
      || bEventType === COMPLETED_REVIEW_JOURNEY_APPLICATION
  );

  const eventAIsJAStartedApplicationSourceWF = (
    aEventType === STARTED_APPLICATION
      && aNodeType === SOURCE_WORKFLOW
  );

  const eventBIsJAStartedApplicationSourceWF = (
    bEventType === STARTED_APPLICATION
      && bNodeType === SOURCE_WORKFLOW
  );

  // keep the started_application event with the source_workflow node type first.
  // there's only one event that is started_application event with source_workflow node.
  if (!eventAIsJAStartedApplicationSourceWF && eventBIsJAStartedApplicationSourceWF) {
    return 1;
  }
  if (eventAIsJAStartedApplicationSourceWF && !eventBIsJAStartedApplicationSourceWF) {
    return -1;
  }

  // Push all JA level events to the end
  if (!eventAIsJA && eventBIsJA) {
    return -1;
  }
  if (eventAIsJA && !eventBIsJA) {
    return 1;
  }
  if (eventAIsJA && eventBIsJA) {
    return 0;
  }

  // Now that we know both events are on the EA level, sort by entity_token
  if (aToken < bToken) {
    return -1;
  }
  if (aToken > bToken) {
    return 1;
  }
  return 0;
});

const formatEntities = entities => {
  const [businessNames, personNames] = entities.reduce(([b, p], e) => {
    if (e.name && groupTypes.includes(e.type)) b.push(e.name);
    if (e.name && !groupTypes.includes(e.type)) p.push(e.name);

    return [b, p];
  }, [[], []]);

  return {
    name: businessNames.join(', ') || personNames.join(', '),
    type: getEntitiesType(entities),
  };
};

const getEntitiesType = entities => {
  if (entities.some(e => e.type === business)) return business;
  if (entities.length > 1) return group;
  return 'person';
};

const formatChildEntityData = ({
  entities,
  journeySteps,
}) => entities.map(entity => {
  const {
    name,
    token,
    type,
    entity_application_branch_name: entityApplicationBranchName,
    entity_application_outcome: entityApplicationOutcome,
    entity_application_status: entityApplicationStatus,
    entity_application_token: entityApplicationToken,
    external_entity_identifier: externalEntityIdentifier,
    sandbox_persona_name: sandboxPersonaName,
  } = entity || {};

  return {
    name,
    token,
    type,
    entityApplicationBranchName,
    entityApplicationOutcome,
    entityApplicationStatus,
    entityApplicationToken,
    externalEntityIdentifier,
    sandboxPersonaName,
    journeySteps: journeySteps.filter(step => (
      step.entityApplicationToken === entityApplicationToken
        || (
          !step.entityApplicationToken
            && step.nodeType === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW
        )
        || (
          step.entityApplicationToken === null
            && step.nodeType === JourneyNodeTypesMap.SOURCE_WORKFLOW
        ) || (step.entityApplicationToken === null && step.review)
    )),
  };
});

const formatAssignedAgent = assignedAgents => {
  if (!assignedAgents?.length) {
    return null;
  }

  // For now, only ever 1 assigned agent
  const [{ agentFirstName, agentLastName }] = assignedAgents;
  return `${agentFirstName} ${agentLastName}`;
};

export const formatApplication = ({
  application,
}) => {
  const entities = application._embedded.child_entities || [];
  const journeySteps = _sortBy(
    formatJourneySteps({
      entities,
      events: sortEventsByEntity(application._embedded.events),
    }),
    'visitedNodeAt',
  );

  const mecToken = application._embedded.multi_environment_config?.multi_environment_config_token;
  const journeyType = application._embedded.journey.journey_type;
  const journeyAlertCase = (
    journeyType === JourneyTypesMap.ALERT && application._embedded.case?.case_token
      ? application._embedded.case
      : null
  );

  const isAlert = journeyType === JourneyTypesMap.ALERT;

  const reviewEvents = application._embedded.events
    .filter(event => (
      isAlert
        ? event.type === JourneyApplicationEventTypesMap.COMPLETED_REVIEW
        : event.type === JourneyApplicationEventTypesMap.COMPLETED_REVIEW_JOURNEY_APPLICATION
    ));

  return {
    journeySteps,
    childEntities: formatChildEntityData({
      entities,
      events: application._embedded.events,
      journeySteps,
    }),
    entityApplications: formatEntityApplications(application._embedded.entity_applications
      || []),
    groupApplicationOutcome: application.complete_outcome,
    groupApplicationStatus: application.status,
    groupEntity: formatEntityData(application._embedded.entity),
    ...(journeyAlertCase && {
      journeyAlertCase,
    }),
    journeyApplicationCreatedAt: application.created_at,
    journeyApplicationIsEscalated: application.is_escalated,
    journeyApplicationStatus: application.journey_application_status,
    journeyApplicationState: application.status,
    journeyApplicationToken: application.journey_application_token,
    journeyApplicationUpdatedAt: application.updated_at,
    journeyChampionChallenger: application._embedded.journey_champion_challenger || null,
    journeyName: application._embedded.journey.journey_name,
    journeyType,
    journeyToken: application._embedded.journey.journey_token,
    journeyVersionNum: application._embedded.journey.journey_version_num,
    lastReview: reviewEvents[reviewEvents.length - 1],
    sandbox: application.sandbox,
    sandboxPersonaTestToken: application.sandbox_persona_test_token,
    isShadowApplication: application.is_shadow_application,
    isPartOfShadowTest: application.is_part_of_shadow_test, // this field is true for the "parent"/"active" application of the shadow test as well as for the "shadow" apps, whereas `isShadowApp` is true only for the "shadow" apps.
    shadowTestToken: application.shadow_test_token,
    multiEnvironmentConfigToken: mecToken,
    rerun: {
      isRerun: application?.is_rerun || null,
      initiatedBy: application?._embedded?.rerun?.initiated_by || null,
      previousJAToken: application?._embedded?.rerun?.previous_journey_application_token || null,
      rootJAToken: application?._embedded?.rerun?.root_journey_application_token || null,
      createdAt: application?._embedded?.rerun?.created_at || null,
    },
    reruns: application?._embedded?.reruns,
    externalApplicationId: application.external_application_id,
  };
};

export const formatApplicationRows = (applications, childCustomers = []) => {
  const auditedChildren = childCustomers
    ?.filter(child => child?.access_type === 'full_audit' || child?.access_type === 'partial_audit')
    .map(child => child?.organization_name_slug);

  return applications.map(application => {
    const {
      name: entityName,
      type: entityType,
    } = formatEntities((application._embedded.child_entities || []));

    return ({
      entityName,
      entityType,
      assignedAgent: formatAssignedAgent(application.assignedAgents),
      journeyApplicationCreatedAt: application.journeyApplicationCreatedAt,
      journeyApplicationId: application.journeyApplicationId,
      counterparties: application.counterparties,
      journeyApplicationIsEscalated: application.journeyApplicationIsEscalated,
      journeyApplicationStatus: application.journeyApplicationStatus,
      journeyApplicationOutcomeColor: application.journeyApplicationOutcomeColor ? `#${application.journeyApplicationOutcomeColor}` : null,
      journeyApplicationOutcomeIconName: application.journeyApplicationOutcomeIconName ?? null,
      journeyApplicationToken: application.journeyApplicationToken,
      journeyApplicationUpdatedAt: application.journeyApplicationUpdatedAt,
      journeyNameObject: {
        journeyName: application.journeyName,
        journeyPublicVersion: application.journeyPublicVersion,
      },
      journeyType: application.journeyType,
      journeyToken: application.journeyToken,
      organizationName: application.organizationName,
      organizationNameSlug: application.organizationNameSlug,
      cannotEdit: auditedChildren.includes(application?.organizationNameSlug),
      note: application?.note,
      outcomeReasons: application?.outcomeReasons,
      reason: application?.reason,
      type: snakeToCaps(application?.eventType || application?.sourceWorkflowType),
    });
  });
};

export const getAllEvaluationTokens = (
  journeySteps,
  groupEntityToken,
  applicationEntityTokens,
) => journeySteps
  .filter(step => (
    step.entityToken
    || (
      (
        step.evaluationToken
        || step.portfolioEvaluationToken
      ) && step.nodeType === JourneyNodeTypesMap.SOURCE_WORKFLOW
    ) || step.nodeType === 'reconciliation_workflow'),
  )
  .flatMap(
    ({
      evaluationTokensSortedByCreatedDesc = [],
      entityToken,
      nodeType,
      workflow,
    }) => {
      // SoureWorkflows must have the entityToken of the individual entity, not the group entity, otherwise the TRX service can not find the right transactions
      if (
        nodeType === JourneyNodeTypesMap.SOURCE_WORKFLOW
        && workflow?.type !== 'portfolio' // Dont replace the entityToken for portfolio evaluations
      ) {
        return evaluationTokensSortedByCreatedDesc.map(evaluationToken => ({
          evaluationToken,
          entityToken: applicationEntityTokens.at(0) ?? groupEntityToken,
        }));
      }

      return evaluationTokensSortedByCreatedDesc.map(evaluationToken => ({
        evaluationToken,
        entityToken: entityToken ?? groupEntityToken,
      }));
    },
  );

export const formatCase = ({
  case_token: caseToken,
  _embedded: { works },
}) => {
  const closureWork = works.find(({ type }) => type === 'closure');
  return ({
    caseToken,
    createdAt: closureWork.created_at,
    noteObjects: works.map(work => ({
      author: work._embedded.created_by.name,
      createdAt: work.created_at,
      note: work.note,
    })),
    outcome: closureWork.status,
    reason: (closureWork.reasons || [])[0],
    reasons: closureWork.reasons || [],
    reviewerName: closureWork._embedded.created_by.name,
  });
};

export const formatAlertManualReview = alertManualReview => {
  if (!alertManualReview) {
    return {};
  }

  const reviewerName = alertManualReview.reviewed_by
    ? `${alertManualReview.reviewed_by.first_name} ${alertManualReview.reviewed_by.last_name}`
    : null;

  return {
    alertManualReviewToken: alertManualReview.alert_manual_review_token,
    createdAt: alertManualReview.reviewed_at,
    noteObjects: alertManualReview.note
      ? [{
        author: reviewerName,
        createdAt: alertManualReview.reviewed_at,
        note: alertManualReview.note,
      }]
      : [],
    outcome: alertManualReview.outcome,
    reason: alertManualReview.reason,
    reasons: [alertManualReview.reason],
    reviewerName,
    investigationToken: alertManualReview._embedded?.investigation?.investigation_token ?? null,
  };
};

export const formatDate = date => date && new Date(date)
  .toLocaleDateString(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    month: 'short',
    year: 'numeric',
  });

export const formatDocumentForUploadedDocumentComponent = ({
  doc,
  entityToken,
}) => ({
  ...doc,
  // this value should be should be based on the doc extension in types_map in
  // mimetypes.py, but we can assume the document is an image and the following is all that
  // is needed for the component:
  approval_outcome: doc.approved,
  content_type: 'image/',
  filename: doc.name,
  review_timestamp: formatDate(doc.approval_timestamp),
  document_type: doc.type,
  formatted_timestamp: formatDate(doc.timestamp),
  notes: doc.notes.map(note => ({
    ...note,
    created_at: note.created_at ? new Date(note.created_at) : null,
    text: note.text,
    formatted_created_at: note.created_at && new Date(note.created_at)
      .toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
      }),
  })),
  entity_token: entityToken,
});

export const getOutcomeOptionsByReviewNodeId = ({
  allowedJourneyOutcomes = [], // allowedJourneyOutcomes will only be populated for journey alerts in a terminal state
  applicationEvents,
  eojReconWfOutcomes = [],
  journeyActions,
  journeyType,
  journeyVersionHasOldOutcomes,
  outcomes,
}) => {
  const {
    alertOutcomes,
    applicationOutcomes,
    map: outcomesByName,
  } = (outcomes || []).reduce((agg, outcome) => {
    if (outcome.outcome) {
      const outcomeName = normalizeString(outcome.outcome);

      // index outcomes as select options by name for faster access
      if (agg.map[outcomeName]) {
        /**
         * System or unarchived outcomes should override the existing outcome if there are
         * dupes and it's either not a system outcome or is archived
         */
        if (
          (outcome.is_system_outcome && !agg.map[outcomeName].is_system_outcome)
          || (!outcome.archived && agg.map[outcomeName].archived)
        ) {
          agg.map[outcomeName] = {
            id: outcome.customer_outcome_id,
            value: outcome.outcome,
            archived: outcome.archived,
            is_application_outcome: outcome.is_application_outcome,
            is_system_outcome: outcome.is_system_outcome,
          };
        }
      } else {
        agg.map[outcomeName] = {
          id: outcome.customer_outcome_id,
          value: outcome.outcome,
          archived: outcome.archived,
          is_application_outcome: outcome.is_application_outcome,
          is_system_outcome: outcome.is_system_outcome,
        };
      }

      if (outcome.is_application_outcome && !outcome.archived) {
        agg.applicationOutcomes.push(agg.map[outcomeName]);
      }

      if (outcome.is_alert_outcome && !outcome.archived) {
        agg.alertOutcomes.push(agg.map[outcomeName]);
      }
    }

    return agg;
  }, {
    alertOutcomes: [],
    applicationOutcomes: [],
    map: {},
  });

  // will be undefined if no journeyActions are passed
  const outcomeOptionsByCustomReviewActionId = journeyActions
    ?.filter(action => action.active && (
      action.type === ACTION_NODE_TYPES.CUSTOM_REVIEW
        || action.type === ACTION_NODE_TYPES.PARENT_REVIEW
    ))
    .reduce((map, action) => {
      const normalizedOutcomes = action
        .outcomes
        .map(outcomeName => {
          if (outcomeName) {
            return outcomesByName[normalizeString(outcomeName)];
          }
          return null;
        })
        .filter(outcome => !!outcome);
      map[action.journey_action_id] = normalizedOutcomes;
      return map;
    }, {});

  const manualReviewOutcomeOptions = [
    ...(journeyType === JourneyTypesMap.ALERT && !journeyVersionHasOldOutcomes
      ? _uniqBy(
        [...allowedJourneyOutcomes, ...ALERTS_MANUAL_REVIEW_OUTCOMES_NAMES],
        outcome => outcome.toLowerCase(),
      )
      : DEFAULT_MANUAL_REVIEW_OUTCOMES_NAMES
    ),
  ]
    // grab only the default outcomes that are part of the customer's outcomes
    // as of now, only "withdrawn" sits behind a backend feature flag
    .filter(outcomeName => (outcomeName.toLowerCase() in outcomesByName))
    .map(outcomeName => {
      if (outcomeName) {
        return outcomesByName[normalizeString(outcomeName)];
      }
      return null;
    })
    .sort((outcomeA, outcomeB) => outcomeA.id - outcomeB.id);

  const customReviewEvents = applicationEvents.filter(event => (
    event._embedded.node.type === JourneyNodeTypesMap.ACTION
    && (
      event._embedded.node.config.action_type === ACTION_NODE_TYPES.CUSTOM_REVIEW
      || event._embedded.node.config.action_type === ACTION_NODE_TYPES.PARENT_REVIEW
    )
  ));

  const defaultManualReviewEvents = applicationEvents.filter(event => (
    event._embedded.node.type === JourneyNodeTypesMap.REVIEW
  ));


  const outcomeOptionsByReviewNodeId = {};
  if (outcomeOptionsByCustomReviewActionId) {
    customReviewEvents.forEach(event => {
      const { id: nodeId } = event._embedded.node;
      outcomeOptionsByReviewNodeId[
        nodeId
      ] = outcomeOptionsByCustomReviewActionId[event._embedded.node.config.journey_action_id];
    });
  }

  // manual review outcomes will only be populated if an applicationEvent of type review was reached.
  defaultManualReviewEvents.forEach(event => {
    const { id: nodeId } = event._embedded.node;
    outcomeOptionsByReviewNodeId[
      nodeId
    ] = manualReviewOutcomeOptions;
  });

  /**
   * With custom Journey Application outcomes, the allowed outcomes on an EOJ recon node
   * and post-EOJ recon manual review node will change.
   *
   * Since everything on the Journey graph post EOJ recon node is only visual and not
   * stored we need to figure out the allowed outcomes for post-EOJ recon manual
   * reviews here.
   *
   * Visually, EOJ WF nodes will display all outcomes used by the WF that are also valid
   * Application Journey outcomes (incl Approved, Denied, Manual Review that are allowed
   * today). If the Manual Review outcome is used, it will lead to a manual review node.
   * That node/review will include all other outcomes shown on the EOJ WF node
   * (excl. Manual Review) and will always include the default review options `Approved`/`Denied`.
   * If Manual Review is the only outcome displayed from the recon WF node, the default review
   * options `Approved`/`Denied` will be the only allowed outcomes options for the review.
   *
   * This logic is to enumerate the allowed outcomes for the post-EOJ recon manual review form.
   */
  let allowedEojReviewOutcomes = [];
  if (eojReconWfOutcomes?.length) {
    allowedEojReviewOutcomes = eojReconWfOutcomes
      .filter(reconOutcome => {
        const outcomeName = reconOutcome.outcome && normalizeString(reconOutcome.outcome);

        return (
          (outcomeName in outcomesByName)
          && outcomesByName[outcomeName].is_application_outcome
          && !outcomesByName[outcomeName].archived
          && outcomeName.toLowerCase() !== MANUAL_REVIEW_OUTCOME.toLowerCase()
        );
      })
      .map(({ outcome }) => outcomesByName[normalizeString(outcome)]);
  }

  if (journeyType !== JourneyTypesMap.ALERT) {
    outcomeOptionsByReviewNodeId[
      DEFAULT_POST_EOJ_RECON_MANUAL_REVIEW_NODE_ID
    ] = _uniqBy(
      [...allowedEojReviewOutcomes, ...manualReviewOutcomeOptions],
      outcome => outcome.id,
    );
  }

  /**
   * After all outcomes are indexed by their node id, we manually index the default manual review.
   * This is needed for reviews that are not tied to an individual node, such as the
   * application-wide review.
   */
  outcomeOptionsByReviewNodeId[
    DEFAULT_MANUAL_REVIEW_NODE_ID
  ] = manualReviewOutcomeOptions;

  outcomeOptionsByReviewNodeId[
    POST_COMPLETED_JA_MANUAL_REVIEW_NODE_ID
  ] = journeyType === JourneyTypesMap.APPLICATION
    ? applicationOutcomes
    : alertOutcomes;

  return Object.keys(outcomeOptionsByReviewNodeId).reduce((agg, key) => {
    /**
     * Remove the archived/is_application_outcome fields added earlier
     * as they're not required outside of the logic of this util
     */
    agg[key] = (outcomeOptionsByReviewNodeId[key] ?? [])?.map(outcome => ({
      id: outcome.id,
      value: outcome.value,
    })).sort((a, b) => a.value?.localeCompare(b.value));

    return agg;
  }, {});
};

export const getReviewReasonsByOutcomeId = ({
  outcomes,
  reasons,
}) => {
  const applicationReviewOutcomeIdsSet = outcomes.reduce(
    (acc, { customer_outcome_id: outcomeId }) => {
      acc.add(outcomeId);
      return acc;
    }, new Set(),
  );

  const applicationReviewReasonObjectsByOutcomeId = reasons
    .filter(({ customer_outcome_id: outcomeId }) => (
      !outcomeId || applicationReviewOutcomeIdsSet.has(outcomeId)
    ))
    .reduce((acc, {
      customer_outcome_id,
      customer_reason_id: id,
      default_note: defaultNote,
      reason,
    }) => {
      const outcomeId = customer_outcome_id || 'noId';
      if (!acc[outcomeId]) {
        acc[outcomeId] = [];
      }
      acc[outcomeId].push({ defaultNote, id, value: reason });
      return acc;
    }, {});

  return applicationReviewReasonObjectsByOutcomeId;
};


export const getCasesByCaseToken = cases => cases.reduce(
  (acc, _case) => {
    acc[_case.case_token] = formatCase(_case);
    return acc;
  }, {},
);

export const getAlertReviewsByAlertManualReviewToken = manualReviews => manualReviews.reduce(
  (acc, manualReview) => {
    acc[manualReview.alert_manual_review_token] = formatAlertManualReview(manualReview);
    return acc;
  }, {},
);

export const getCompletedCaseTokens = journeySteps => (journeySteps || [])
  .flatMap(step => step.reviews || [])
  .filter(review => (
    review?.caseToken
    && (
      review.nodeType === 'review'
      || review?.action?.type === ACTION_NODE_TYPES.CUSTOM_REVIEW
      || review?.action?.type === ACTION_NODE_TYPES.PARENT_REVIEW
    )
    && review.status !== 'Pending'
  )).map(review => review.caseToken);

export const getCompletedAlertManualReviewTokens = journeySteps => (
  (journeySteps || [])
    .flatMap(step => step.reviews || [])
    .filter(review => (
      review?.alertManualReviewToken
      && (
        review.nodeType === 'review'
        || review?.action?.type === ACTION_NODE_TYPES.CUSTOM_REVIEW
        || review?.action?.type === ACTION_NODE_TYPES.PARENT_REVIEW
      )
      && review.status !== 'Pending'
    )).map(review => review.alertManualReviewToken)
);

export const getEntityDocumentsByDocToken = entityDocuments => entityDocuments
  .reduce((acc, doc) => {
    acc[doc.document_token] = doc;
    return acc;
  }, {});

export const getEvaluationsByEvaluationToken = (
  evaluations,
  eventsByEventRequestToken,
) => evaluations.reduce(
  (acc, evaluation) => {
    /**
     * Handling the scenario where an eval fetched in JA details encountered a `404` and we
     * handled gracefully by returning `null` in `application-review/redux/thunks.ts`
     */
    if (!evaluation?.event) {
      return acc;
    }

    const {
      event_type: eventType,
      data,
    } = eventsByEventRequestToken[evaluation.event.event_request_token] ?? {};

    evaluation.event.event_request_type = eventType;
    evaluation.event.event_request_data = data;

    acc[evaluation.event.evaluation_token] = evaluation;
    return acc;
  }, {},
);

export const getEventsByEventRequestToken = events => events.reduce(
  (acc, { event_request_token: eventRequestToken, body }) => {
    acc[eventRequestToken] = body;
    return acc;
  }, {},
);

const formatReviewNotes = reviewCases => {
  const reviewCaseWorks = (reviewCases || []).map(c => c._embedded.works);
  return ([].concat(...reviewCaseWorks))
    .reduce((notes, work) => {
      if (work.note) {
        notes.push({
          agent_email: work._embedded.created_by.email,
          agent_name: work._embedded.created_by.name,
          created_at: new Date(work.created_at).toISOString(),
          formatted_created_at: new Date(work.created_at)
            .toLocaleDateString(undefined, {
              day: 'numeric',
              year: 'numeric',
              month: 'short',
            }),
          formatted_updated_at: new Date(work.created_at)
            .toLocaleDateString(undefined, {
              day: 'numeric',
              year: 'numeric',
              month: 'short',
            }),
          text: work.note,
          updated_at: new Date(work.created_at).toISOString(),
          journey_application_id: '',
        });
      }

      return notes;
    }, []);
};

export const getJourneyApplicationNotes = (entities, reviewCases) => {
  if ((!entities || !entities.length) && (!reviewCases || !reviewCases.length)) {
    return [];
  }

  const [{ notes: entityNotes = [] }] = entities;
  const reviewNotes = formatReviewNotes(reviewCases);

  const journeyApplicationNotes = [
    ...entityNotes.filter(n => n.journey_application_id),
    ...reviewNotes,
  ];

  // Daffy naively assumes the entity notes are in UTC, while the review notes use the local time zone. So we have to remove the UTC designation 'Z' without actually converting to the local time zone...
  return journeyApplicationNotes.sort((a, b) => new Date(b.created_at.replace('Z', '')) - new Date(a.created_at.replace('Z', '')));
};

export const flattenReviewsJourneySteps = journeySteps => (journeySteps || [])
  .reduce((acc, step) => {
    acc.push(step);
    if (step?.reviews) {
      acc.push(step.reviews);
    } else if (step?.review) {
      acc.push(step.review);
    }
    return acc;
  }, [])
  .flat();

export const getInitialSelectedJourneyApplicationStep = (journeySteps, journeyType) => {
  // The review time of a node should be treated as the node time
  const stepsByRecency = journeySteps
    .filter(step => (
      step.nodeType !== 'reconciliation_workflow'
      && step.action?.type !== ACTION_NODE_TYPES.CUSTOM_REVIEW
      && step.action?.type !== ACTION_NODE_TYPES.PARENT_REVIEW
    ))
    .sort((step1, step2) => {
      const time1 = step1.review?.visitedNodeAt || step1.visitedNodeAt;
      const time2 = step2.review?.visitedNodeAt || step2.visitedNodeAt;

      return time1 - time2;
    });

  // NOTE:  Alerts should default to the first step
  //        Applications should default to most recent
  const initialStepIdx = journeyType === JourneyTypesMap.ALERT ? 0 : stepsByRecency.length - 1;
  const initialStep = stepsByRecency[initialStepIdx];
  let caseToken = null;
  if (journeyType !== JourneyTypesMap.ALERT && initialStep?.reviews) {
    const completedCase = initialStep.reviews
      .filter(review => (
        review.nodeType === 'review'
        || review.action?.type === ACTION_NODE_TYPES.CUSTOM_REVIEW
        || review.action?.type === ACTION_NODE_TYPES.PARENT_REVIEW
      ) && review.status !== 'Pending')
      .sort((a, b) => a.visitedNodeAt - b.visitedNodeAt);
    const lastReview = completedCase.pop();
    caseToken = lastReview?.caseToken;
  }
  return {
    journeyApplicationEventToken: initialStep?.journeyApplicationEventToken,
    entityToken: initialStep?.entityToken,
    caseToken,
  };
};

export const getJourneyStepsByJourneyApplicationEventToken = journeySteps => journeySteps.reduce(
  (acc, journeyStep) => {
    acc[journeyStep.journeyApplicationEventToken] = journeyStep;
    return acc;
  }, {},
);

export const getJourneyApplicationReviewCaseTokens = events => events
  .filter(event => event.type === 'completed_review_journey_application')
  .map(event => event.case_token);

export const convertStateVarsToQueryParams = ({
  filterValuesByColumn,
  numRowsPerPage,
  pageNum,
  searchValuesByColumn,
  sortBy,
}) => `?${queryString.stringify(Object
  .entries({
    filterValuesByColumn,
    numRowsPerPage,
    pageNum,
    searchValuesByColumn,
    sortBy,
  })
  .reduce((acc, [key, val]) => ({
    ...acc,
    [STATE_VAR_TO_QUERY_PARAM_NAME[key]]: JSON.stringify(val),
  }), {}))}`;

const parseQueryParamsStr = queryParamsStr => Object
  .entries(queryString.parse(queryParamsStr))
  .filter(([paramName, val]) => (
    QUERY_PARAM_NAME_TO_STATE_VAR[paramName]
    && val
  ))
  .map(([paramName, val]) => {
    try {
      return [paramName, JSON.parse(val)];
    } catch {
      return [paramName, val];
    }
  })
  .reduce((acc, [paramName, val]) => {
    acc[paramName] = val;
    return acc;
  }, {});

export const applicationTableFormatters = {
  convertStateVarsToQueryParams,
  parseQueryParamsStr,
};

/**
 * Returns a map of journeyActionIds to all their journeyActionVersionIds
 * @param {*} journeyActions
 * @returns {Record<string, string[]>}
 */
export const getJourneyActionVersionsByJourneyActionId = journeyActions => {
  const transformedJourneyActions = {};

  journeyActions.forEach(({
    journey_action_id, journey_action_version_id,
  }) => {
    if (!transformedJourneyActions[journey_action_id]) {
      transformedJourneyActions[journey_action_id] = [];
    }

    transformedJourneyActions[journey_action_id].push(String(journey_action_version_id));
  });

  return transformedJourneyActions;
};
